import React, { useState, useRef, useEffect } from 'react';
import { TIME_RANGES, getTimeRangePreset, getAvailableRanges, DEFAULT_PRESET } from '../../constants/timeRanges';

const TimeRangeSelector = ({
  selectedRange,
  onRangeChange,
  disabled = false,
  onRefresh = null,
  isRefreshing = false,
  preset = DEFAULT_PRESET, // New preset-based approach
  // Legacy props for backward compatibility (will be deprecated)
  showCustomRange = null,
  allowedRanges = null,
  maxRange = null,
  rangeMap = null
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const [customRange, setCustomRange] = useState({
    from: new Date(Date.now() - 24 * 60 * 60 * 1000), // 24 hours ago
    to: new Date(),
    fromTime: '00:00',
    toTime: '23:59'
  });

  const dropdownRef = useRef(null);
  const modalRef = useRef(null);

  // Get configuration from preset or legacy props
  const config = React.useMemo(() => {
    // If legacy props are provided, use them (backward compatibility)
    if (allowedRanges !== null || showCustomRange !== null || maxRange !== null || rangeMap !== null) {
      return {
        allowedRanges,
        showCustomRange: showCustomRange ?? true,
        maxRange: maxRange ?? 90,
        rangeMap: rangeMap ?? {}
      };
    }
    
    // Use preset configuration
    return getTimeRangePreset(preset);
  }, [preset, allowedRanges, showCustomRange, maxRange, rangeMap]);

  // Get available time ranges based on configuration
  const availableTimeRanges = React.useMemo(() => {
    if (config.allowedRanges) {
      // Create a flat list of allowed ranges without sections
      const flatRanges = [];
      TIME_RANGES.forEach(section => {
        section.ranges.forEach(range => {
          if (config.allowedRanges.includes(range.value)) {
            flatRanges.push(range);
          }
        });
      });
      return [{ section: '', ranges: flatRanges }];
    }
    return TIME_RANGES;
  }, [config.allowedRanges]);

  // Get display text for current selection
  const getCurrentDisplayText = () => {
    if (typeof selectedRange === 'object' && selectedRange.type === 'custom') {
        const fromDate = new Date(selectedRange.from);
        const toDate = new Date(selectedRange.to);
        return `${formatDate(fromDate)} - ${formatDate(toDate)}`;
    }

    // Use rangeMap for custom display text
    if (config.rangeMap[selectedRange]) {
        return config.rangeMap[selectedRange];
    }

    // Find preset display text
    for (const section of TIME_RANGES) {
        const range = section.ranges.find(r => r.value === selectedRange);
        if (range) return range.display;
    }

    return 'Last 7 days'; // Default fallback
};

  // Format date for display
  const formatDate = (date) => {
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = date.toDateString() === new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString();
    
    if (isToday) {
      return `Today ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
    } else if (isYesterday) {
      return `Yesterday ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
    } else {
      return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
    }
  };

  // Format date for input fields
  const formatDateForInput = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Format time for input fields
  const formatTimeForInput = (date) => {
    return date.toTimeString().slice(0, 5);
  };

  // Handle preset selection
  const handlePresetSelect = (value) => {
    onRangeChange(value);
    setIsDropdownOpen(false);
  };

  // Handle custom range selection
  const handleCustomRangeSelect = () => {
    setIsDropdownOpen(false);
    setIsCustomModalOpen(true);
    
    // Initialize custom range based on current selection
    const now = new Date();
    if (typeof selectedRange === 'object' && selectedRange.type === 'custom') {
      setCustomRange({
        from: new Date(selectedRange.from),
        to: new Date(selectedRange.to),
        fromTime: formatTimeForInput(new Date(selectedRange.from)),
        toTime: formatTimeForInput(new Date(selectedRange.to))
      });
    } else {
      setCustomRange({
        from: new Date(now.getTime() - 24 * 60 * 60 * 1000),
        to: now,
        fromTime: '00:00',
        toTime: '23:59'
      });
    }
  };

  // Apply custom range
  const applyCustomRange = () => {
    const fromDateTime = new Date(customRange.from);
    const toDateTime = new Date(customRange.to);
    
    // Set time components
    const [fromHour, fromMinute] = customRange.fromTime.split(':');
    const [toHour, toMinute] = customRange.toTime.split(':');
    
    fromDateTime.setHours(parseInt(fromHour), parseInt(fromMinute), 0, 0);
    toDateTime.setHours(parseInt(toHour), parseInt(toMinute), 59, 999);
    
    // Validation
    if (fromDateTime >= toDateTime) {
      alert('From date/time must be before To date/time');
      return;
    }
    
    const diffDays = (toDateTime - fromDateTime) / (1000 * 60 * 60 * 24);
    if (diffDays > config.maxRange) {
      alert(`Date range cannot exceed ${config.maxRange} days`);
      return;
    }
    
    const customRangeObject = {
      type: 'custom',
      from: fromDateTime.toISOString(),
      to: toDateTime.toISOString(),
      display: `${formatDate(fromDateTime)} - ${formatDate(toDateTime)}`
    };
    
    onRangeChange(customRangeObject);
    setIsCustomModalOpen(false);
  };

  // Quick preset buttons for custom modal
  const quickPresets = [
    { label: 'Last hour', action: () => {
      const now = new Date();
      const hourAgo = new Date(now.getTime() - 60 * 60 * 1000);
      setCustomRange({
        from: hourAgo,
        to: now,
        fromTime: formatTimeForInput(hourAgo),
        toTime: formatTimeForInput(now)
      });
    }},
    { label: 'Today', action: () => {
      const now = new Date();
      const startOfDay = new Date(now);
      startOfDay.setHours(0, 0, 0, 0);
      setCustomRange({
        from: startOfDay,
        to: now,
        fromTime: '00:00',
        toTime: formatTimeForInput(now)
      });
    }},
    { label: 'Yesterday', action: () => {
      const now = new Date();
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const startOfYesterday = new Date(yesterday);
      startOfYesterday.setHours(0, 0, 0, 0);
      const endOfYesterday = new Date(yesterday);
      endOfYesterday.setHours(23, 59, 59, 999);
      setCustomRange({
        from: startOfYesterday,
        to: endOfYesterday,
        fromTime: '00:00',
        toTime: '23:59'
      });
    }},
    { label: 'This week', action: () => {
      const now = new Date();
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      setCustomRange({
        from: startOfWeek,
        to: now,
        fromTime: '00:00',
        toTime: formatTimeForInput(now)
      });
    }}
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsCustomModalOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
        setIsCustomModalOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <div className="time-range-selector-container">
      {/* Main dropdown button */}
      <div className="time-range-dropdown" ref={dropdownRef}>
        <button
          className={`time-range-main-button ${isDropdownOpen ? 'open' : ''}`}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          disabled={disabled}
        >
          <span className="time-range-display">{getCurrentDisplayText()}</span>
          <span className="dropdown-arrow">▼</span>
        </button>

        {/* Dropdown menu */}
        {isDropdownOpen && (
          <div className="time-range-dropdown-menu">
            {availableTimeRanges.map((section) => (
              <div key={section.section} className="time-range-section">
                {section.section && <div className="time-range-section-header">{section.section}</div>}
                {section.ranges.map((range) => (
                  <button
                    key={range.value}
                    className={`time-range-option ${selectedRange === range.value ? 'active' : ''}`}
                    onClick={() => handlePresetSelect(range.value)}
                  >
                    {config.rangeMap[range.value] || range.label}
                  </button>
                ))}
              </div>
            ))}
            
            {config.showCustomRange && (
              <div className="time-range-section">
                <div className="time-range-section-header">Custom</div>
                <button
                  className={`time-range-option ${typeof selectedRange === 'object' && selectedRange.type === 'custom' ? 'active' : ''}`}
                  onClick={handleCustomRangeSelect}
                >
                  Custom range...
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Refresh button */}
      {onRefresh && (
        <button
          className={`time-range-refresh-button ${isRefreshing ? 'refreshing' : ''}`}
          onClick={onRefresh}
          disabled={disabled || isRefreshing}
          title="Refresh data (Ctrl+R)"
        >
          <span className="refresh-icon">⟳</span>
        </button>
      )}

      {/* Custom range modal */}
      {isCustomModalOpen && (
        <div className="time-range-modal-overlay">
          <div className="time-range-modal" ref={modalRef}>
            <div className="time-range-modal-header">
              <h3>Select Custom Time Range</h3>
              <button 
                className="time-range-modal-close"
                onClick={() => setIsCustomModalOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="time-range-modal-content">
              {/* Quick presets */}
              <div className="time-range-quick-presets">
                <label>Quick presets:</label>
                <div className="quick-preset-buttons">
                  {quickPresets.map((preset) => (
                    <button
                      key={preset.label}
                      className="quick-preset-button"
                      onClick={preset.action}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date/time inputs */}
              <div className="time-range-inputs">
                <div className="time-range-input-group">
                  <label>From:</label>
                  <div className="datetime-input">
                    <input
                      type="date"
                      value={formatDateForInput(customRange.from)}
                      onChange={(e) => setCustomRange({
                        ...customRange,
                        from: new Date(e.target.value)
                      })}
                    />
                    <input
                      type="time"
                      value={customRange.fromTime}
                      onChange={(e) => setCustomRange({
                        ...customRange,
                        fromTime: e.target.value
                      })}
                    />
                  </div>
                </div>

                <div className="time-range-input-group">
                  <label>To:</label>
                  <div className="datetime-input">
                    <input
                      type="date"
                      value={formatDateForInput(customRange.to)}
                      onChange={(e) => setCustomRange({
                        ...customRange,
                        to: new Date(e.target.value)
                      })}
                    />
                    <input
                      type="time"
                      value={customRange.toTime}
                      onChange={(e) => setCustomRange({
                        ...customRange,
                        toTime: e.target.value
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="time-range-preview">
                <strong>Preview:</strong> {formatDate(new Date(customRange.from))} - {formatDate(new Date(customRange.to))}
              </div>
            </div>

            <div className="time-range-modal-footer">
              <button 
                className="time-range-cancel-button"
                onClick={() => setIsCustomModalOpen(false)}
              >
                Cancel
              </button>
              <button 
                className="time-range-apply-button"
                onClick={applyCustomRange}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeRangeSelector;
